import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { server } from '../../constants';

const jwtInterceptor = axios.create({
  withCredentials: true, // Ensures cookies are sent with requests
});

let refreshTokenPromise: Promise<AxiosResponse> | null = null;
let retryCount = 0;
const MAX_RETRIES = 2;
let isRefreshing = false;

// Store the timestamp when the token was last refreshed
let lastTokenRefresh = 0;
// Minimum time between token refresh attempts (15 seconds)
const MIN_REFRESH_INTERVAL = 15000;

jwtInterceptor.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only attempt to refresh token for 401 errors
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      retryCount < MAX_RETRIES
    ) {
      // Prevent multiple simultaneous refresh attempts
      const currentTime = Date.now();
      if (
        isRefreshing ||
        currentTime - lastTokenRefresh < MIN_REFRESH_INTERVAL
      ) {
        // If already refreshing or refreshed recently, wait for the existing refresh to complete
        if (refreshTokenPromise) {
          try {
            await refreshTokenPromise;
            // Try the original request again
            return axios(originalRequest);
          } catch {
            // If the refresh failed, we should still reject
            return Promise.reject(error);
          }
        } else {
          // No refresh in progress but we're in the cooldown period
          return Promise.reject(error);
        }
      }

      // Mark that we're attempting a refresh and this request has been retried
      isRefreshing = true;
      originalRequest._retry = true;
      retryCount++;

      try {
        // Only create a new refresh token promise if one doesn't exist
        if (!refreshTokenPromise) {
          refreshTokenPromise = axios.get(`${server}/auth/refreshToken`, {
            withCredentials: true,
            timeout: 10000, // Reduced timeout to 10 seconds - much more reasonable
          });
        }

        // Wait for the refresh
        await refreshTokenPromise;

        // Update the last refresh timestamp
        lastTokenRefresh = Date.now();

        // Reset the refresh state
        isRefreshing = false;
        refreshTokenPromise = null;

        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        // Reset states on failure
        isRefreshing = false;
        refreshTokenPromise = null;
        retryCount = 0;

        // If refresh fails, redirect to login or handle as needed
        // This is cleaner than throwing a potentially unhandled rejection
        console.error('Token refresh failed:', refreshError);

        // Return the original error to maintain expected behavior
        return Promise.reject(error);
      }
    }

    // Reset retry count for non-auth errors or max retries exceeded
    if (error.response?.status !== 401 || retryCount >= MAX_RETRIES) {
      retryCount = 0;
    }

    return Promise.reject(error);
  }
);

export default jwtInterceptor;
